import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { Diamond, Clock, Lock, ImageOff, Loader2 } from 'lucide-react';
import { Lightbox } from '@/components/Lightbox';
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { getSharedGallery, downloadSharedImage } from '@/lib/api';
import { SHARED_GALLERY_PAGE, BEFORE_AFTER } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';

const BREAKPOINTS = { default: 3, 1024: 3, 768: 2, 500: 1 };

export default function SharedGallery() {
  const { token } = useParams();
  const [category, setCategory] = useState(null);
  const [error, setError] = useState(null); // 'expired' | 'invalid' | null
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setCategory(null);
    setError(null);
    getSharedGallery(token)
      .then(setCategory)
      .catch((err) => setError(err?.response?.status === 410 ? 'expired' : 'invalid'));
  }, [token]);

  const handleDownload = async (image, resolution = 'original') => {
    try {
      const { blob, filename } = await downloadSharedImage(token, image.id, resolution);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Downloading ${filename}`);
    } catch (err) {
      toast.error('Download failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid={SHARED_GALLERY_PAGE.root}>
      <SEO title="Shared Gallery Preview" description="A private photo preview shared by LXRA Studios." />

      <header className="border-b border-[hsl(var(--lxra-border))] px-4 sm:px-6 lg:px-10 py-5">
        <div className="mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
            <Diamond className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
            <span className="font-display tracking-[0.15em] text-sm">
              LXRA <span className="text-[hsl(var(--lxra-muted))] font-light">STUDIOS</span>
            </span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--lxra-muted))] border border-[hsl(var(--lxra-border))] rounded-full px-3 py-1.5">
            Shared Preview
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
        {!category && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="col-span-full flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--lxra-gold))]" />
            </div>
            <SkeletonList count={6} className="h-72 rounded-xl bg-[#141416]" />
          </div>
        )}

        {error && (
          <div
            className="mx-auto max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center"
            data-testid={SHARED_GALLERY_PAGE.invalid}
          >
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
              {error === 'expired' ? <Clock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
            </span>
            <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">
              {error === 'expired' ? 'This link has expired' : 'Link not found'}
            </h2>
            <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
              {error === 'expired'
                ? 'This preview link is no longer active. Please ask your photographer for a fresh one.'
                : "This shared link is invalid or has been revoked. Please double-check the URL or ask your photographer to resend it."}
            </p>
          </div>
        )}

        {category && (
          <>
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">Private Preview</p>
              <h1 className="font-display text-3xl sm:text-4xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-2">{category.name}</h1>
              {category.description && <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] max-w-2xl">{category.description}</p>}
            </div>

            {category.images.length === 0 ? (
              <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
                <ImageOff className="h-8 w-8 text-[hsl(var(--lxra-muted))] mx-auto mb-3" />
                <p className="text-sm text-[hsl(var(--lxra-muted))]">No photos have been added to this gallery yet.</p>
              </div>
            ) : (
              <div data-testid={SHARED_GALLERY_PAGE.grid}>
                <Masonry breakpointCols={BREAKPOINTS} className="masonry-grid" columnClassName="masonry-grid_column">
                  {category.images.map((img, idx) => (
                    <Reveal key={img.id} delay={(idx % 6) * 0.04}>
                      <button
                        onClick={() => setLightboxIndex(idx)}
                        data-testid={SHARED_GALLERY_PAGE.image(idx)}
                        className="group relative block w-full min-h-[180px] overflow-hidden rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416]"
                      >
                        <img
                          src={img.image_url}
                          alt={img.caption || `${category.name} photo ${idx + 1}`}
                          loading="eager"
                          className="w-full object-cover hover-zoom-img"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                      </button>
                    </Reveal>
                  ))}
                </Masonry>
              </div>
            )}

            {category.before_after?.length > 0 && (
              <div className="mt-16" data-testid={BEFORE_AFTER.section}>
                <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] mb-1">Before &amp; After</p>
                <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mb-6">Drag to Compare</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {category.before_after.map((pair) => (
                    <Reveal key={pair.id}>
                      <BeforeAfterSlider before={pair.before} after={pair.after} caption={pair.caption} testId={BEFORE_AFTER.slider(pair.id)} />
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {category && (
        <Lightbox
          images={category.images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}
